import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Eye, EyeOff, ArrowRight, ArrowLeft, Check, User, Mail, Lock, 
    Calendar, Phone, Globe, TrendingUp, Target, Shield, Zap,
    Bitcoin, Wallet, ChevronRight, Sparkles, Award, Trophy, Star,
    ChevronDown
} from 'lucide-react';
import confetti from 'canvas-confetti';

const LOGO_URL = "/logo.jpg";

// Country list for dropdown
const COUNTRIES = [
    "United States", "United Kingdom", "Canada", "Australia", "Germany", "France", 
    "Japan", "South Korea", "Singapore", "Hong Kong", "Switzerland", "Netherlands",
    "Sweden", "Norway", "Denmark", "Finland", "Ireland", "New Zealand", "Austria",
    "Belgium", "Spain", "Italy", "Portugal", "Poland", "Czech Republic", "Brazil",
    "Mexico", "Argentina", "Chile", "Colombia", "India", "Indonesia", "Malaysia",
    "Thailand", "Vietnam", "Philippines", "United Arab Emirates", "Saudi Arabia",
    "Israel", "South Africa", "Nigeria", "Egypt", "Turkey", "Russia", "Ukraine",
    "Romania", "Hungary", "Greece", "Croatia", "Slovenia", "Estonia", "Latvia",
    "Lithuania", "Luxembourg", "Malta", "Cyprus", "Iceland", "Other"
].sort();

// Crypto Matrix Rain Background Component
const CryptoMatrixRain = () => {
    const canvasRef = useRef(null);
    
    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);
        
        const cryptoSymbols = ['₿', 'Ξ', '₮', '$', '◎', '₳', '⟠', 'Ł', '◈', '✧', '⬡', '⬢', '◆', '●', '■'];
        const fontSize = 16;
        const columns = Math.floor(canvas.width / fontSize);
        
        const drops = Array(columns).fill(1);
        const speeds = Array(columns).fill(0).map(() => Math.random() * 0.5 + 0.5);
        const symbols = Array(columns).fill('').map(() => 
            cryptoSymbols[Math.floor(Math.random() * cryptoSymbols.length)]
        );
        
        const colors = {
            '₿': '#F7931A', 'Ξ': '#627EEA', '₮': '#26A17B', '$': '#2775CA',
            '◎': '#9945FF', '₳': '#0033AD', '⟠': '#E84142', 'Ł': '#345D9D',
            'default': '#5BA4A4'
        };
        
        const draw = () => {
            ctx.fillStyle = 'rgba(5, 5, 5, 0.05)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.font = `${fontSize}px monospace`;
            
            for (let i = 0; i < drops.length; i++) {
                if (Math.random() > 0.98) {
                    symbols[i] = cryptoSymbols[Math.floor(Math.random() * cryptoSymbols.length)];
                }
                
                const symbol = symbols[i];
                const color = colors[symbol] || colors.default;
                
                if (Math.random() > 0.9) {
                    ctx.shadowBlur = 10;
                    ctx.shadowColor = color;
                } else {
                    ctx.shadowBlur = 0;
                }
                
                const opacity = Math.min(drops[i] * fontSize / canvas.height, 0.8);
                ctx.fillStyle = color + Math.floor(opacity * 255).toString(16).padStart(2, '0');
                ctx.fillText(symbol, i * fontSize, drops[i] * fontSize);
                
                if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
                    drops[i] = 0;
                    speeds[i] = Math.random() * 0.5 + 0.5;
                }
                drops[i] += speeds[i];
            }
        };
        
        const interval = setInterval(draw, 33);
        return () => {
            clearInterval(interval);
            window.removeEventListener('resize', resizeCanvas);
        };
    }, []);
    
    return (
        <canvas 
            ref={canvasRef} 
            className="fixed inset-0 z-0"
            style={{ background: 'linear-gradient(180deg, #050505 0%, #0a0a0a 100%)' }}
        />
    );
};

// Achievement Badge Component
const AchievementBadge = ({ icon: Icon, label, unlocked, color = "#5BA4A4" }) => (
    <motion.div
        className={`flex flex-col items-center gap-1 p-2 rounded-lg ${unlocked ? 'opacity-100' : 'opacity-30'}`}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${unlocked ? 'bg-gradient-to-br from-[#5BA4A4]/30 to-transparent border border-[#5BA4A4]' : 'bg-gray-800 border border-gray-700'}`}>
            <Icon size={18} style={{ color: unlocked ? color : '#4b5563' }} />
        </div>
        <span className={`text-[10px] ${unlocked ? 'text-[#5BA4A4]' : 'text-gray-600'}`}>{label}</span>
    </motion.div>
);

// Profile Completion Bar
const ProfileCompletionBar = ({ percentage, achievements }) => (
    <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-400 uppercase tracking-wider">Profile Completion</span>
            <span className="text-sm font-mono text-[#5BA4A4]">{percentage}%</span>
        </div>
        <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
            <motion.div 
                className="h-full bg-gradient-to-r from-[#5BA4A4] to-[#7BC4C4]"
                initial={{ width: 0 }}
                animate={{ width: `${percentage}%` }}
                transition={{ duration: 0.5 }}
            />
        </div>
        <div className="flex justify-center gap-2 mt-3">
            {achievements.map((ach, i) => (
                <AchievementBadge key={i} {...ach} />
            ))}
        </div>
    </div>
);

// Step indicator component
const StepIndicator = ({ currentStep, totalSteps }) => (
    <div className="flex items-center justify-center gap-2 mb-4">
        {[...Array(totalSteps)].map((_, i) => (
            <motion.div
                key={i}
                className={`h-2 rounded-full transition-all duration-300 ${
                    i < currentStep 
                        ? 'bg-[#5BA4A4] w-8' 
                        : i === currentStep 
                            ? 'bg-[#5BA4A4] w-12' 
                            : 'bg-gray-700 w-8'
                }`}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: i * 0.1 }}
            />
        ))}
    </div>
);

// Crypto chip selector
const CryptoChip = ({ crypto, selected, onClick }) => (
    <motion.button
        type="button"
        onClick={onClick}
        className={`px-3 py-2 rounded-lg border transition-all flex items-center gap-2 ${
            selected 
                ? 'bg-[#5BA4A4]/20 border-[#5BA4A4] text-[#5BA4A4]' 
                : 'bg-black/30 border-gray-700 text-gray-400 hover:border-gray-500'
        }`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
    >
        {selected && <Check size={14} />}
        <span className="text-sm font-medium">{crypto}</span>
    </motion.button>
);

// Option card for selections
const OptionCard = ({ icon: Icon, title, description, selected, onClick, color = "#5BA4A4" }) => (
    <motion.button
        type="button"
        onClick={onClick}
        className={`w-full p-4 rounded-xl border text-left transition-all ${
            selected 
                ? 'bg-gradient-to-br from-[rgba(91,164,164,0.15)] to-transparent border-[#5BA4A4]' 
                : 'bg-black/30 border-gray-700/50 hover:border-gray-600'
        }`}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
    >
        <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${selected ? 'bg-[#5BA4A4]/20' : 'bg-gray-800'}`}>
                <Icon size={20} style={{ color: selected ? color : '#6b7280' }} />
            </div>
            <div className="flex-1">
                <div className="flex items-center justify-between">
                    <h4 className={`font-semibold ${selected ? 'text-white' : 'text-gray-300'}`}>{title}</h4>
                    {selected && <Check size={16} className="text-[#5BA4A4]" />}
                </div>
                <p className="text-xs text-gray-500 mt-1">{description}</p>
            </div>
        </div>
    </motion.button>
);

// Country Dropdown Component
const CountryDropdown = ({ value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredCountries = COUNTRIES.filter(c => 
        c.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full px-4 py-3 bg-black/50 border border-[rgba(91,164,164,0.3)] rounded-lg text-left flex items-center justify-between hover:border-[#5BA4A4] transition-colors"
            >
                <span className={value ? 'text-white' : 'text-gray-600'}>
                    {value || 'Select your country'}
                </span>
                <ChevronDown size={18} className={`text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute z-50 w-full mt-1 bg-[#0a0a0a] border border-[rgba(91,164,164,0.3)] rounded-lg shadow-xl max-h-60 overflow-hidden"
                    >
                        <div className="p-2 border-b border-gray-800">
                            <input
                                type="text"
                                placeholder="Search countries..."
                                className="w-full px-3 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-600 focus:border-[#5BA4A4] outline-none"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                            {filteredCountries.map((country) => (
                                <button
                                    key={country}
                                    type="button"
                                    className={`w-full px-4 py-2 text-left text-sm hover:bg-[#5BA4A4]/10 transition-colors ${
                                        value === country ? 'bg-[#5BA4A4]/20 text-[#5BA4A4]' : 'text-gray-300'
                                    }`}
                                    onClick={() => {
                                        onChange(country);
                                        setIsOpen(false);
                                        setSearch('');
                                    }}
                                >
                                    {country}
                                </button>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default function RegisterPage() {
    const [step, setStep] = useState(0);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        dateOfBirth: '',
        phone: '',
        country: '',
        tradingExperience: '',
        riskTolerance: '',
        preferredCryptos: ['BTC', 'ETH'],
        investmentGoal: '',
        referralCode: '',
        accountManager: false
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    const totalSteps = 4;

    const cryptoOptions = ['BTC', 'ETH', 'SOL', 'XRP', 'ADA', 'DOGE', 'MATIC', 'AVAX', 'DOT', 'LINK'];

    const experienceLevels = [
        { id: 'beginner', icon: Sparkles, title: 'Beginner', description: "I'm new to crypto trading" },
        { id: 'intermediate', icon: TrendingUp, title: 'Intermediate', description: 'I have some trading experience' },
        { id: 'advanced', icon: Target, title: 'Advanced', description: 'I actively trade and understand markets' },
        { id: 'expert', icon: Zap, title: 'Expert', description: 'Professional trader with years of experience' }
    ];

    const riskLevels = [
        { id: 'conservative', icon: Shield, title: 'Conservative', description: 'Preserve capital, steady growth', color: '#22c55e' },
        { id: 'moderate', icon: TrendingUp, title: 'Moderate', description: 'Balanced risk and reward', color: '#eab308' },
        { id: 'aggressive', icon: Zap, title: 'Aggressive', description: 'High risk for high returns', color: '#ef4444' }
    ];

    const investmentGoals = [
        { id: 'hodl', icon: Bitcoin, title: 'Long-term HODL', description: 'Buy and hold for years' },
        { id: 'day_trading', icon: Zap, title: 'Day Trading', description: 'Multiple trades daily' },
        { id: 'swing_trading', icon: TrendingUp, title: 'Swing Trading', description: 'Hold positions for days/weeks' },
        { id: 'passive_income', icon: Wallet, title: 'Passive Income', description: 'Staking, yields, dividends' }
    ];

    // Calculate profile completion percentage
    const calculateCompletion = () => {
        let completed = 0;
        const total = 10;
        
        if (formData.name) completed++;
        if (formData.email) completed++;
        if (formData.password && formData.password.length >= 6) completed++;
        if (formData.phone) completed++;
        if (formData.country) completed++;
        if (formData.tradingExperience) completed++;
        if (formData.riskTolerance) completed++;
        if (formData.preferredCryptos.length > 0) completed++;
        if (formData.investmentGoal) completed++;
        if (formData.dateOfBirth) completed++;
        
        return Math.round((completed / total) * 100);
    };

    // Get achievements based on form completion
    const getAchievements = () => [
        { icon: User, label: 'Identity', unlocked: formData.name && formData.email, color: '#5BA4A4' },
        { icon: Shield, label: 'Security', unlocked: formData.password && formData.password.length >= 6, color: '#22c55e' },
        { icon: Target, label: 'Trader', unlocked: formData.tradingExperience && formData.riskTolerance, color: '#eab308' },
        { icon: Bitcoin, label: 'Crypto Fan', unlocked: formData.preferredCryptos.length >= 3, color: '#F7931A' },
        { icon: Trophy, label: 'Complete', unlocked: calculateCompletion() === 100, color: '#a855f7' }
    ];

    const toggleCrypto = (crypto) => {
        setFormData(prev => ({
            ...prev,
            preferredCryptos: prev.preferredCryptos.includes(crypto)
                ? prev.preferredCryptos.filter(c => c !== crypto)
                : [...prev.preferredCryptos, crypto]
        }));
    };

    const validateStep = () => {
        switch (step) {
            case 0:
                if (!formData.name.trim()) { toast.error('Please enter your name'); return false; }
                if (!formData.email.trim()) { toast.error('Please enter your email'); return false; }
                if (!/\S+@\S+\.\S+/.test(formData.email)) { toast.error('Please enter a valid email'); return false; }
                return true;
            case 1:
                if (!formData.password) { toast.error('Please enter a password'); return false; }
                if (formData.password.length < 6) { toast.error('Password must be at least 6 characters'); return false; }
                if (formData.password !== formData.confirmPassword) { toast.error('Passwords do not match'); return false; }
                return true;
            case 2:
                if (!formData.tradingExperience) { toast.error('Please select your experience level'); return false; }
                if (!formData.riskTolerance) { toast.error('Please select your risk tolerance'); return false; }
                return true;
            case 3:
                if (formData.preferredCryptos.length === 0) { toast.error('Please select at least one cryptocurrency'); return false; }
                return true;
            default:
                return true;
        }
    };

    const triggerConfetti = () => {
        confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#5BA4A4', '#7BC4C4', '#F7931A', '#627EEA', '#a855f7']
        });
    };

    const nextStep = () => {
        if (validateStep()) {
            if (step < totalSteps - 1) {
                setStep(step + 1);
                // Trigger mini confetti when completing a step
                if (step === 1) {
                    confetti({ particleCount: 30, spread: 40, origin: { y: 0.7 } });
                }
            } else {
                handleSubmit();
            }
        }
    };

    const prevStep = () => {
        if (step > 0) setStep(step - 1);
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            await register(
                formData.email, 
                formData.password, 
                formData.name, 
                formData.dateOfBirth || null,
                {
                    phone: formData.phone || null,
                    country: formData.country || null,
                    trading_experience: formData.tradingExperience,
                    risk_tolerance: formData.riskTolerance,
                    preferred_cryptos: formData.preferredCryptos,
                    investment_goal: formData.investmentGoal || null,
                    referral_code: formData.referralCode || null
                }
            );
            
            // Trigger celebration confetti
            triggerConfetti();
            setTimeout(() => triggerConfetti(), 200);
            
            toast.success('Welcome to The Cybernetics! 🚀', {
                description: 'Your trading journey begins now!'
            });
            
            setTimeout(() => navigate('/dashboard'), 1500);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    const renderStep = () => {
        switch (step) {
            case 0:
                return (
                    <motion.div
                        key="step0"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-3 md:space-y-4"
                    >
                        <div className="text-center mb-3 md:mb-4">
                            <h2 className="text-lg md:text-xl font-bold text-white mb-1">Let's get started</h2>
                            <p className="text-xs md:text-sm text-gray-500">Tell us about yourself</p>
                        </div>

                        <div>
                            <label className="block text-[10px] md:text-xs text-gray-400 uppercase tracking-wider mb-1.5 md:mb-2 flex items-center gap-1.5">
                                <User size={10} className="md:w-3 md:h-3" /> Full Name
                            </label>
                            <input
                                type="text"
                                className="w-full px-3 md:px-4 py-2.5 md:py-3 bg-black/50 border border-[rgba(91,164,164,0.3)] rounded-lg text-white placeholder-gray-600 focus:border-[#5BA4A4] outline-none transition-colors text-sm"
                                placeholder="Enter your full name"
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                data-testid="register-name-input"
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] md:text-xs text-gray-400 uppercase tracking-wider mb-1.5 md:mb-2 flex items-center gap-1.5">
                                <Mail size={10} className="md:w-3 md:h-3" /> Email Address
                            </label>
                            <input
                                type="email"
                                className="w-full px-3 md:px-4 py-2.5 md:py-3 bg-black/50 border border-[rgba(91,164,164,0.3)] rounded-lg text-white placeholder-gray-600 focus:border-[#5BA4A4] outline-none transition-colors text-sm"
                                placeholder="Enter your email"
                                value={formData.email}
                                onChange={(e) => setFormData({...formData, email: e.target.value})}
                                data-testid="register-email-input"
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-[10px] md:text-xs text-gray-400 uppercase tracking-wider mb-1.5 md:mb-2 flex items-center gap-1.5">
                                    <Phone size={10} className="md:w-3 md:h-3" /> Phone (Optional)
                                </label>
                                <input
                                    type="tel"
                                    className="w-full px-3 md:px-4 py-2.5 md:py-3 bg-black/50 border border-[rgba(91,164,164,0.3)] rounded-lg text-white placeholder-gray-600 focus:border-[#5BA4A4] outline-none transition-colors text-sm"
                                    placeholder="+1 234 567 8900"
                                    value={formData.phone}
                                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] md:text-xs text-gray-400 uppercase tracking-wider mb-1.5 md:mb-2 flex items-center gap-1.5">
                                    <Globe size={10} className="md:w-3 md:h-3" /> Country
                                </label>
                                <CountryDropdown 
                                    value={formData.country}
                                    onChange={(country) => setFormData({...formData, country})}
                                />
                            </div>
                        </div>

                        {/* Account Manager Toggle */}
                        <div className="mt-3 md:mt-4 p-3 md:p-4 bg-gradient-to-br from-[rgba(91,164,164,0.1)] to-transparent border border-[rgba(91,164,164,0.2)] rounded-lg md:rounded-xl">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2 md:gap-3 min-w-0">
                                    <div className="p-1.5 md:p-2 bg-[#5BA4A4]/20 rounded-lg flex-shrink-0">
                                        <User size={14} className="md:w-[18px] md:h-[18px] text-[#5BA4A4]" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-xs md:text-sm font-semibold text-white truncate">Account Manager</p>
                                        <p className="text-[10px] md:text-xs text-gray-500 truncate">Get dedicated support</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setFormData({...formData, accountManager: !formData.accountManager})}
                                    className={`relative w-12 md:w-14 h-6 md:h-7 rounded-full transition-colors flex-shrink-0 ${
                                        formData.accountManager ? 'bg-[#5BA4A4]' : 'bg-gray-700'
                                    }`}
                                    data-testid="account-manager-toggle"
                                >
                                    <motion.div
                                        className="absolute top-1 w-4 md:w-5 h-4 md:h-5 bg-white rounded-full shadow-md"
                                        animate={{ left: formData.accountManager ? '26px' : '4px' }}
                                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                    />
                                </button>
                            </div>
                            {formData.accountManager && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    className="mt-2 md:mt-3 pt-2 md:pt-3 border-t border-[rgba(91,164,164,0.2)]"
                                >
                                    <div className="flex items-center gap-2 text-[10px] md:text-xs text-[#5BA4A4]">
                                        <Check size={12} className="md:w-3.5 md:h-3.5 flex-shrink-0" />
                                        <span>Premium support and guided trading assistance</span>
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    </motion.div>
                );

            case 1:
                return (
                    <motion.div
                        key="step1"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-4"
                    >
                        <div className="text-center mb-4">
                            <h2 className="text-xl font-bold text-white mb-1">Secure your account</h2>
                            <p className="text-sm text-gray-500">Create a strong password</p>
                        </div>

                        <div>
                            <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                                <Lock size={12} /> Password
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    className="w-full px-4 py-3 bg-black/50 border border-[rgba(91,164,164,0.3)] rounded-lg text-white placeholder-gray-600 focus:border-[#5BA4A4] outline-none transition-colors pr-12"
                                    placeholder="Create a strong password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                                    data-testid="register-password-input"
                                />
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 p-1"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                            <div className="mt-2 flex gap-1">
                                {[...Array(4)].map((_, i) => (
                                    <div 
                                        key={i}
                                        className={`h-1 flex-1 rounded-full ${
                                            formData.password.length > i * 3 
                                                ? i < 2 ? 'bg-red-500' : i < 3 ? 'bg-yellow-500' : 'bg-green-500'
                                                : 'bg-gray-700'
                                        }`}
                                    />
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                                <Lock size={12} /> Confirm Password
                            </label>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                className="w-full px-4 py-3 bg-black/50 border border-[rgba(91,164,164,0.3)] rounded-lg text-white placeholder-gray-600 focus:border-[#5BA4A4] outline-none transition-colors"
                                placeholder="Confirm your password"
                                value={formData.confirmPassword}
                                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                                data-testid="register-confirm-password-input"
                            />
                            {formData.confirmPassword && formData.password === formData.confirmPassword && (
                                <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                                    <Check size={12} /> Passwords match
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                                <Calendar size={12} /> Date of Birth (Optional)
                            </label>
                            <input
                                type="date"
                                className="w-full px-4 py-3 bg-black/50 border border-[rgba(91,164,164,0.3)] rounded-lg text-white focus:border-[#5BA4A4] outline-none transition-colors"
                                value={formData.dateOfBirth}
                                onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})}
                            />
                        </div>
                    </motion.div>
                );

            case 2:
                return (
                    <motion.div
                        key="step2"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-4"
                    >
                        <div className="text-center mb-4">
                            <h2 className="text-xl font-bold text-white mb-1">Trading Profile</h2>
                            <p className="text-sm text-gray-500">Help us personalize your experience</p>
                        </div>

                        <div>
                            <label className="block text-xs text-gray-400 uppercase tracking-wider mb-3">
                                Your Trading Experience
                            </label>
                            <div className="space-y-2">
                                {experienceLevels.map((level) => (
                                    <OptionCard
                                        key={level.id}
                                        icon={level.icon}
                                        title={level.title}
                                        description={level.description}
                                        selected={formData.tradingExperience === level.id}
                                        onClick={() => setFormData({...formData, tradingExperience: level.id})}
                                    />
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs text-gray-400 uppercase tracking-wider mb-3 mt-4">
                                Risk Tolerance
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                                {riskLevels.map((level) => (
                                    <motion.button
                                        key={level.id}
                                        type="button"
                                        onClick={() => setFormData({...formData, riskTolerance: level.id})}
                                        className={`p-3 rounded-xl border text-center transition-all ${
                                            formData.riskTolerance === level.id 
                                                ? 'border-[#5BA4A4] bg-[#5BA4A4]/10' 
                                                : 'border-gray-700 bg-black/30 hover:border-gray-600'
                                        }`}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        <level.icon size={20} className="mx-auto mb-2" style={{ color: level.color }} />
                                        <p className="text-xs font-medium text-white">{level.title}</p>
                                    </motion.button>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                );

            case 3:
                return (
                    <motion.div
                        key="step3"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-4"
                    >
                        <div className="text-center mb-4">
                            <h2 className="text-xl font-bold text-white mb-1">Almost there!</h2>
                            <p className="text-sm text-gray-500">Select your preferences</p>
                        </div>

                        <div>
                            <label className="block text-xs text-gray-400 uppercase tracking-wider mb-3">
                                Preferred Cryptocurrencies
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {cryptoOptions.map((crypto) => (
                                    <CryptoChip
                                        key={crypto}
                                        crypto={crypto}
                                        selected={formData.preferredCryptos.includes(crypto)}
                                        onClick={() => toggleCrypto(crypto)}
                                    />
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs text-gray-400 uppercase tracking-wider mb-3 mt-4">
                                Investment Goal (Optional)
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                {investmentGoals.map((goal) => (
                                    <motion.button
                                        key={goal.id}
                                        type="button"
                                        onClick={() => setFormData({...formData, investmentGoal: goal.id})}
                                        className={`p-3 rounded-xl border text-left transition-all ${
                                            formData.investmentGoal === goal.id 
                                                ? 'border-[#5BA4A4] bg-[#5BA4A4]/10' 
                                                : 'border-gray-700 bg-black/30 hover:border-gray-600'
                                        }`}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        <goal.icon size={18} className="text-[#5BA4A4] mb-2" />
                                        <p className="text-sm font-medium text-white">{goal.title}</p>
                                        <p className="text-[10px] text-gray-500">{goal.description}</p>
                                    </motion.button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2 mt-4">
                                Referral Code (Optional)
                            </label>
                            <input
                                type="text"
                                className="w-full px-4 py-3 bg-black/50 border border-[rgba(91,164,164,0.3)] rounded-lg text-white placeholder-gray-600 focus:border-[#5BA4A4] outline-none transition-colors"
                                placeholder="Enter referral code"
                                value={formData.referralCode}
                                onChange={(e) => setFormData({...formData, referralCode: e.target.value})}
                            />
                        </div>
                    </motion.div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen relative overflow-hidden overflow-x-hidden">
            <CryptoMatrixRain />
            <div className="fixed inset-0 z-1 bg-gradient-to-b from-transparent via-black/30 to-black/60 pointer-events-none" />
            
            <div className="relative z-10 min-h-screen flex items-center justify-center p-3 md:p-4 py-6 md:py-8">
                <motion.div 
                    className="w-full max-w-md bg-[#0a0a0a]/90 backdrop-blur-xl border border-[rgba(91,164,164,0.3)] rounded-xl md:rounded-2xl p-4 md:p-6 shadow-2xl"
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    style={{ boxShadow: '0 0 60px rgba(91,164,164,0.15)' }}
                >
                    {/* Logo */}
                    <motion.div 
                        className="flex flex-col items-center mb-3"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        <div className="relative mb-2">
                            <div className="absolute inset-0 bg-[#5BA4A4] blur-2xl opacity-30 rounded-full scale-150" />
                            <img src={LOGO_URL} alt="The Cybernetics" className="relative w-12 h-12 rounded-xl border border-[rgba(91,164,164,0.3)]"/>
                        </div>
                        <h1 className="text-base font-bold text-[#5BA4A4] uppercase tracking-wider">The Cybernetics</h1>
                    </motion.div>

                    {/* Profile Completion Bar */}
                    <ProfileCompletionBar 
                        percentage={calculateCompletion()} 
                        achievements={getAchievements()}
                    />

                    {/* Step Indicator */}
                    <StepIndicator currentStep={step} totalSteps={totalSteps} />

                    {/* Form Steps */}
                    <form onSubmit={(e) => { e.preventDefault(); nextStep(); }}>
                        <AnimatePresence mode="wait">
                            {renderStep()}
                        </AnimatePresence>

                        {/* Navigation Buttons */}
                        <div className="flex gap-3 mt-5">
                            {step > 0 && (
                                <motion.button
                                    type="button"
                                    onClick={prevStep}
                                    className="flex-1 py-3 bg-transparent border border-gray-700 text-gray-400 font-medium rounded-lg hover:bg-gray-800/50 transition-all flex items-center justify-center gap-2"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <ArrowLeft size={18} />
                                    Back
                                </motion.button>
                            )}
                            <motion.button
                                type="submit"
                                className={`flex-1 py-3 bg-[#5BA4A4] text-black font-bold rounded-lg hover:bg-[#4a9393] transition-all flex items-center justify-center gap-2 disabled:opacity-50 ${step === 0 ? 'w-full' : ''}`}
                                disabled={loading}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                data-testid="register-submit-btn"
                            >
                                {loading ? (
                                    'Creating account...'
                                ) : step === totalSteps - 1 ? (
                                    <>
                                        Create Account
                                        <Sparkles size={18} />
                                    </>
                                ) : (
                                    <>
                                        Continue
                                        <ArrowRight size={18} />
                                    </>
                                )}
                            </motion.button>
                        </div>
                    </form>

                    {/* Login Link */}
                    <div className="mt-4 text-center text-sm text-gray-500">
                        Already have an account?{' '}
                        <Link to="/login" className="text-[#5BA4A4] hover:underline" data-testid="login-link">
                            Sign in
                        </Link>
                        <span className="mx-2 text-gray-700">·</span>
                        <Link to="/portal?mode=register" className="text-amber-400 hover:underline" data-testid="register-change-portal">Recovery Wallet?</Link>
                    </div>

                    {/* Skip Button for optional steps */}
                    {step >= 2 && (
                        <button
                            type="button"
                            onClick={() => step < totalSteps - 1 ? setStep(step + 1) : handleSubmit()}
                            className="w-full mt-2 text-xs text-gray-600 hover:text-gray-400 transition-colors"
                        >
                            Skip for now <ChevronRight size={12} className="inline" />
                        </button>
                    )}
                </motion.div>
            </div>
        </div>
    );
}
